import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AssignRouteModal, {
  AssignRouteFormValues,
} from "app/components/Modal/AssignRouteModal";
import { MobilePageHeader } from "app/mobile/components/MobilePageHeader";
import { KnowledgePanel } from "app/components/Knowledge/KnowledgePanel";
import {
  MobileRoutesFilterDrawer,
  RouteStateFilter,
} from "app/mobile/components/MobileRoutesUi";
import { useAuthContext } from "app/components/Auth/AuthContext";
import {
  AuthData,
  clearAuthData,
  getOdooApiHeaders,
  getStoredAuthData,
  isEmbeddedMode,
  odooApiFetch,
  subscribeToAuthUpdates,
} from "app/utils/auth";
import {
  buildDeliveryPartnersUrl,
  extractOdooResults,
  serializeOdooFilters,
  splitDeliveryPartners,
  buildAssignableRouteFilters,
  reassignPickingsToRoute,
  DeliveryPartner,
} from "app/utils/odooApi";
import { handleApiError, notifyApiError } from "app/utils/apiErrorHandler";
import { buildAssignPickingsPayload } from "app/utils/assignPickings";
import {
  getInitialDeliveryPartnersState,
  saveDeliveryPartnersToStorage,
} from "app/utils/deliveryPartnersCache";
import {
  ROUTE_STATE_LABELS,
  formatOdooTime,
  getRelationName,
} from "app/utils/odooDisplay";
import { formatUtcScheduledDate, getUserTimezone } from "app/utils/timezone";
import {
  DateRangePresetId,
  getDateRangeForPreset,
  ROUTE_DATE_RANGE_DEFAULT_PRESET,
} from "app/utils/dateRangePresets";
import { DeliveryRoute, UnassignedPicking } from "app/types/route";
import { RouteStopsPanel } from "app/components/RouteStops/RouteStopsPanel";
import { useDeliveryDashboardSettings } from "app/hooks/useDeliveryDashboardSettings";

const ROUTE_STATE_COLORS: Record<string, string> = {
  draft: "#94a3b8",
  assigned: "#3b82f6",
  loaded: "#8b5cf6",
  in_progress: "#f59e0b",
  done: "#22c55e",
  cancelled: "#ef4444",
};

function RouteStateChip({ state }: { state?: string }) {
  if (!state) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={ROUTE_STATE_LABELS[state] || state}
      sx={{
        height: 24,
        bgcolor: `${ROUTE_STATE_COLORS[state] || "#94a3b8"}22`,
        color: ROUTE_STATE_COLORS[state] || "#64748b",
        fontWeight: 600,
        fontSize: "0.72rem",
      }}
    />
  );
}

export function MobileRoutesPage() {
  const embedded = isEmbeddedMode();
  const navigate = useNavigate();
  const contextAuth = useAuthContext();
  const [authData, setAuthData] = useState<AuthData>(
    () => contextAuth || getStoredAuthData() || {}
  );
  const access_token = authData.access_token ?? "";
  const userTimezone = getUserTimezone(authData);

  const [mainTab, setMainTab] = useState(0);
  const [fromDate, setFromDate] = useState<Dayjs | null>(() => {
    const { start } = getDateRangeForPreset(
      ROUTE_DATE_RANGE_DEFAULT_PRESET,
      getUserTimezone(getStoredAuthData())
    );
    return start;
  });
  const [toDate, setToDate] = useState<Dayjs | null>(() => {
    const { end } = getDateRangeForPreset(
      ROUTE_DATE_RANGE_DEFAULT_PRESET,
      getUserTimezone(getStoredAuthData())
    );
    return end;
  });
  const [dateRangePreset, setDateRangePreset] =
    useState<DateRangePresetId | "">(ROUTE_DATE_RANGE_DEFAULT_PRESET);
  const [stateFilter, setStateFilter] = useState<RouteStateFilter>("active");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [knowledgePanelOpen, setKnowledgePanelOpen] = useState(false);
  const [routes, setRoutes] = useState<DeliveryRoute[]>([]);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [routeDetail, setRouteDetail] = useState<DeliveryRoute | null>(null);
  const [unassignedOrders, setUnassignedOrders] = useState<UnassignedPicking[]>([]);
  const [drivers, setDrivers] = useState<Array<{ id: number; name: string }>>(
    () => getInitialDeliveryPartnersState().drivers
  );
  const [depots, setDepots] = useState<Array<{ id: number; name: string }>>(
    () => getInitialDeliveryPartnersState().depots
  );
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isAssigningRoute, setIsAssigningRoute] = useState(false);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [isRefreshingAssignData, setIsRefreshingAssignData] = useState(false);
  const [assignableRoutes, setAssignableRoutes] = useState<DeliveryRoute[]>([]);
  const dashboardSettings = useDeliveryDashboardSettings();

  useEffect(() => {
    if (contextAuth?.access_token) {
      setAuthData(contextAuth);
    }
  }, [contextAuth]);

  useEffect(
    () =>
      subscribeToAuthUpdates((nextAuth) => {
        if (nextAuth) {
          setAuthData(nextAuth);
        }
      }),
    []
  );

  const buildRouteFilters = useCallback(() => {
    const filters: unknown[][] = [];
    if (fromDate) {
      filters.push(["date", ">=", fromDate.format("YYYY-MM-DD")]);
    }
    if (toDate) {
      filters.push(["date", "<=", toDate.format("YYYY-MM-DD")]);
    }
    if (stateFilter === "active") {
      filters.push(["state", "in", ["draft", "assigned", "loaded", "in_progress"]]);
    } else if (stateFilter !== "all") {
      filters.push(["state", "=", stateFilter]);
    }
    return filters;
  }, [fromDate, toDate, stateFilter]);

  const handleFromDateChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }
    const nextFrom = value.startOf("day");
    setDateRangePreset("");
    setFromDate(nextFrom);
    if (toDate && nextFrom.isAfter(dayjs(toDate), "day")) {
      setToDate(nextFrom);
    }
  };

  const handleToDateChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }
    const nextTo = value.startOf("day");
    setDateRangePreset("");
    setToDate(nextTo);
    if (fromDate && nextTo.isBefore(dayjs(fromDate), "day")) {
      setFromDate(nextTo);
    }
  };

  const handleDateRangePresetChange = (preset: DateRangePresetId) => {
    const { start, end } = getDateRangeForPreset(preset, userTimezone);
    setDateRangePreset(preset);
    setFromDate(start);
    setToDate(end);
  };

  const routeDateSubtitle = useMemo(() => {
    if (!fromDate && !toDate) {
      return undefined;
    }
    if (fromDate && toDate && fromDate.isSame(toDate, "day")) {
      return fromDate.format("MM/DD/YYYY");
    }
    if (fromDate && toDate) {
      return `${fromDate.format("MM/DD/YYYY")} – ${toDate.format("MM/DD/YYYY")}`;
    }
    return fromDate?.format("MM/DD/YYYY") || toDate?.format("MM/DD/YYYY");
  }, [fromDate, toDate]);

  const fetchRoutes = useCallback(async () => {
    if (!access_token) return;
    setIsLoadingRoutes(true);
    try {
      const filters = buildRouteFilters();
      const filterQuery =
        filters.length > 0
          ? `filters=${encodeURIComponent(serializeOdooFilters(filters))}&`
          : "";
      const parsed = await odooApiFetch<{ results?: DeliveryRoute[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.route?${filterQuery}order=${encodeURIComponent("name desc")}&limit=100`,
        access_token
      );
      setRoutes(extractOdooResults(parsed));
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Could not load routes.",
      });
    } finally {
      setIsLoadingRoutes(false);
    }
  }, [access_token, buildRouteFilters, embedded]);

  const fetchUnassignedOrders = useCallback(async () => {
    if (!access_token) return;
    setIsLoadingOrders(true);
    try {
      const filters = [
        ["picking_type_code", "=", "outgoing"],
        ["state", "=", "done"],
        ["route_id", "=", false],
        ["company_id", "=", 1],
      ];
      const parsed = await odooApiFetch<{ results?: UnassignedPicking[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/stock.picking/?filters=${encodeURIComponent(
          serializeOdooFilters(filters)
        )}&order=${encodeURIComponent("scheduled_date desc")}&limit=200`,
        access_token
      );
      setUnassignedOrders(extractOdooResults(parsed));
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Could not load unassigned orders.",
      });
    } finally {
      setIsLoadingOrders(false);
    }
  }, [access_token, embedded]);

  const fetchRouteDetail = useCallback(
    async (routeId: number) => {
      if (!access_token) return;
      setIsLoadingDetail(true);
      try {
        const detail = await odooApiFetch<DeliveryRoute>(
          `${process.env.REACT_APP_API_BASE_URL}/delivery.route/${routeId}`,
          access_token
        );
        setRouteDetail(detail);
      } catch (error) {
        setRouteDetail(null);
        await handleApiError(error, {
          embedded,
          fallbackMessage: "Could not load route details.",
          onNotFound: () => {
            setSelectedRouteId(null);
            void fetchRoutes();
          },
        });
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [access_token, embedded, fetchRoutes]
  );

  const fetchAssignableRoutes = useCallback(async () => {
    if (!access_token) return;
    try {
      const filters = buildAssignableRouteFilters({
        fromDate: fromDate?.format("YYYY-MM-DD"),
        toDate: toDate?.format("YYYY-MM-DD"),
      });
      const parsed = await odooApiFetch<{ results?: DeliveryRoute[] }>(
        `${process.env.REACT_APP_API_BASE_URL}/delivery.route?filters=${encodeURIComponent(
          serializeOdooFilters(filters)
        )}&order=${encodeURIComponent("name desc")}&limit=100`,
        access_token
      );
      setAssignableRoutes(extractOdooResults(parsed));
    } catch (error) {
      console.error(error);
      notifyApiError(error, "Could not load assignable routes.");
    }
  }, [access_token, fromDate, toDate]);

  const fetchDriversAndDepots = useCallback(async () => {
    if (!access_token) return;
    try {
      const parsed = await odooApiFetch<{ results?: DeliveryPartner[] }>(
        buildDeliveryPartnersUrl(),
        access_token
      );
      const { drivers: nextDrivers, depots: nextDepots } = splitDeliveryPartners(
        extractOdooResults(parsed)
      );
      setDrivers(nextDrivers);
      setDepots(nextDepots);
      saveDeliveryPartnersToStorage(nextDrivers, nextDepots);
    } catch (error) {
      console.error(error);
      notifyApiError(error, "Could not load drivers and depots.");
    }
  }, [access_token]);

  useEffect(() => {
    if (!access_token) return;
    void fetchRoutes();
  }, [access_token, fetchRoutes]);

  useEffect(() => {
    if (!access_token) return;
    void fetchUnassignedOrders();
  }, [access_token, fetchUnassignedOrders]);

  useEffect(() => {
    if (!access_token || !selectedRouteId) {
      setRouteDetail(null);
      return;
    }
    void fetchRouteDetail(selectedRouteId);
  }, [access_token, selectedRouteId, fetchRouteDetail]);

  const sortedStops = useMemo(() => {
    const stops = Array.isArray(routeDetail?.stop_line_ids)
      ? routeDetail?.stop_line_ids ?? []
      : [];
    return [...stops].sort(
      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
    );
  }, [routeDetail]);

  const handleRefreshAll = () => {
    void fetchRoutes();
    void fetchUnassignedOrders();
    void fetchDriversAndDepots();
    if (selectedRouteId) {
      void fetchRouteDetail(selectedRouteId);
    }
  };

  const handleOpenAssignModal = () => {
    void fetchAssignableRoutes();
    setOpenAssignModal(true);
  };

  const handleStopReleased = async () => {
    await Promise.all([
      selectedRouteId ? fetchRouteDetail(selectedRouteId) : Promise.resolve(),
      fetchUnassignedOrders(),
      fetchRoutes(),
    ]);
  };

  const handleAssignRoute = async (formValues: AssignRouteFormValues) => {
    if (isAssigningRoute || selectedOrderIds.length === 0) return;
    if (formValues.driverId === "") {
      toast.error("Please select a driver.");
      return;
    }
    if (formValues.mode === "existing" && formValues.routeId === "") {
      toast.error("Please select a route.");
      return;
    }
    setIsAssigningRoute(true);
    try {
      const data = await reassignPickingsToRoute(
        buildAssignPickingsPayload(formValues, selectedOrderIds),
        access_token
      );
      toast.success(data?.message || "Deliveries reassigned successfully!");
      setSelectedOrderIds([]);
      setOpenAssignModal(false);
      if (data?.route_id) {
        setSelectedRouteId(data.route_id);
        setMainTab(0);
      }
      await fetchRoutes();
      await fetchUnassignedOrders();
    } catch (error) {
      await handleApiError(error, {
        embedded,
        fallbackMessage: "Failed to assign route.",
      });
    } finally {
      setIsAssigningRoute(false);
    }
  };

  const isBusy = isLoadingRoutes || isLoadingOrders || isLoadingDetail;

  const filtersActive =
    dateRangePreset !== ROUTE_DATE_RANGE_DEFAULT_PRESET ||
    stateFilter !== "active";

  const renderRouteList = () => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
        px: 1.5,
        py: 1.25,
        pb: 3,
      }}
    >
      {isLoadingRoutes ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: "#5fa67a" }} />
        </Box>
      ) : routes.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#9ca3af", textAlign: "center", py: 6 }}>
          No routes for selected date
        </Typography>
      ) : (
        routes.map((route) => (
          <Box
            key={route.id}
            onClick={() => setSelectedRouteId(route.id)}
            sx={{
              mb: 1.25,
              p: 1.5,
              bgcolor: "#fff",
              border: "1px solid #e5e9ef",
              borderRadius: "12px",
              cursor: "pointer",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>{route.name}</Typography>
              <RouteStateChip state={route.state} />
            </Box>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              {route.picking_count ?? 0} orders · {route.total_load_count ?? 0} loads
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );

  const renderUnassignedList = () => (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", px: 1.5, py: 1.25, pb: selectedOrderIds.length ? 12 : 3 }}>
        {isLoadingOrders ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: "#5fa67a" }} />
          </Box>
        ) : unassignedOrders.length === 0 ? (
          <Typography variant="body2" sx={{ color: "#9ca3af", textAlign: "center", py: 6 }}>
            No unassigned orders
          </Typography>
        ) : (
          unassignedOrders.map((order) => (
            <Box
              key={order.id}
              sx={{
                mb: 1.25,
                p: 1.5,
                bgcolor: "#fff",
                border: "1px solid #e5e9ef",
                borderRadius: "12px",
                display: "flex",
                gap: 1,
              }}
            >
              <Checkbox
                checked={selectedOrderIds.includes(order.id)}
                onChange={(_event, checked) => {
                  setSelectedOrderIds((prev) =>
                    checked
                      ? [...prev, order.id]
                      : prev.filter((id) => id !== order.id)
                  );
                }}
                sx={{ p: 0.25, "&.Mui-checked": { color: "#5fa67a" } }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                  {order.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  {getRelationName(order.partner_id, "—")}
                </Typography>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  {formatUtcScheduledDate(order.scheduled_date, userTimezone)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>
      {selectedOrderIds.length > 0 && (
        <Box sx={{ px: 1.5, py: 1, bgcolor: "#fff", borderTop: "1px solid #e5e9ef" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleOpenAssignModal}
            sx={{ bgcolor: "#5fa67a", textTransform: "none", fontWeight: 700 }}
          >
            Assign route ({selectedOrderIds.length})
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderRouteDetail = () => {
    if (!selectedRouteId) return null;
    return (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 20,
          bgcolor: "#f5f6fa",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 1,
            bgcolor: "#fff",
            borderBottom: "1px solid #e5e9ef",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setSelectedRouteId(null);
              setRouteDetail(null);
            }}
            sx={{ textTransform: "none", color: "#374151", minWidth: 0 }}
          >
            Back
          </Button>
        </Box>

        {isLoadingDetail || !routeDetail ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress sx={{ color: "#5fa67a" }} />
          </Box>
        ) : (
          <>
            <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #e5e9ef" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {routeDetail.name}
                </Typography>
                <RouteStateChip state={routeDetail.state} />
              </Box>
              <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                Depot: {getRelationName(routeDetail.depot_id, "—")}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b7280", display: "block" }}>
                {formatOdooTime(routeDetail.start_time)} – {formatOdooTime(routeDetail.finish_time)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <RouteStopsPanel
                routeDetail={routeDetail}
                isLoading={false}
                sortedStops={sortedStops}
                accessToken={access_token}
                embedded={embedded}
                dashboardSettings={dashboardSettings}
                onReleased={handleStopReleased}
                onClose={() => {
                  setSelectedRouteId(null);
                  setRouteDetail(null);
                }}
                fullWidth
              />
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <MobilePageHeader
          title="Routes"
          subtitle={routeDateSubtitle}
          isBusy={isBusy}
          filterActive={filtersActive}
          onOpenFilters={() => setFiltersOpen(true)}
          onOpenKnowledge={() => setKnowledgePanelOpen(true)}
          knowledgeActive={knowledgePanelOpen}
          onRefresh={handleRefreshAll}
          showSignOut={!embedded}
          onSignOut={() => {
            clearAuthData();
            navigate("/login");
          }}
        />

        <Tabs
          value={mainTab}
          onChange={(_event, value) => setMainTab(value)}
          variant="fullWidth"
          sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e9ef", flexShrink: 0 }}
        >
          <Tab label={`Routes (${routes.length})`} />
          <Tab label={`Unassigned (${unassignedOrders.length})`} />
        </Tabs>

        {mainTab === 0 ? renderRouteList() : renderUnassignedList()}
        {renderRouteDetail()}

        <MobileRoutesFilterDrawer
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          stateFilter={stateFilter}
          onStateFilterChange={setStateFilter}
          dateRangePreset={dateRangePreset}
          onDateRangePresetChange={handleDateRangePresetChange}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
        />

        <KnowledgePanel
          open={knowledgePanelOpen}
          onClose={() => setKnowledgePanelOpen(false)}
        />

        {openAssignModal && (
          <AssignRouteModal
            openModal={openAssignModal}
            isSubmitting={isAssigningRoute}
            isRefreshing={isRefreshingAssignData}
            routes={assignableRoutes}
            drivers={drivers}
            depots={depots}
            modalTitle="Reassign Route"
            submitLabel="Reassign Route"
            submittingLabel="Reassigning route..."
            onRefresh={async () => {
              setIsRefreshingAssignData(true);
              try {
                await Promise.all([
                  fetchRoutes(),
                  fetchAssignableRoutes(),
                  fetchDriversAndDepots(),
                ]);
                toast.success("Routes and partners updated.");
              } catch {
                toast.error("Could not refresh routes and partners.");
              } finally {
                setIsRefreshingAssignData(false);
              }
            }}
            handleClose={() => !isAssigningRoute && setOpenAssignModal(false)}
            handleAssignRoute={handleAssignRoute}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
}
